import React, { useEffect, useState, useMemo } from "react";
import AppShell from "@/components/app/AppShell";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Play, CheckCircle2, CalendarClock, Users, ExternalLink, User, Clock } from "lucide-react";
import ClientScheduleAdmin from "@/components/app/ClientScheduleAdmin";
import { useRealtime } from "@/hooks/useRealtime";

const STATUS_COLORS = {
  pending_client_selection: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-slate-100 text-slate-600",
  canceled: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  pending_client_selection: "Awaiting Client",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  canceled: "Canceled",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}

export default function AdminSchedule() {
  const nav = useNavigate();
  const [today, setToday] = useState({ in_progress: [], next: [], later: [], completed: [] });
  const [range, setRange] = useState("today");
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);

  const loadToday = async () => {
    try { const { data } = await api.get("/admin/schedules/today"); setToday(data || { in_progress: [], next: [], later: [], completed: [] }); } catch {}
  };
  const loadRange = async () => {
    try {
      const params = { range };
      if (assigneeFilter !== "all") params.assignee_id = assigneeFilter;
      const { data } = await api.get("/admin/schedules", { params });
      setItems(data || []);
    } catch {}
  };

  useEffect(() => {
    api.get("/admin/team-members").then(({ data }) => setTeam(data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadToday(); }, [refreshKey]);
  useEffect(() => { loadRange(); /* eslint-disable-next-line */ }, [range, assigneeFilter, refreshKey]);

  useRealtime((evt) => {
    if (evt?.type === "notification" && evt.data?.kind?.startsWith("schedule_")) {
      setRefreshKey((k) => k + 1);
    }
  });

  const byAssignee = useMemo(() => {
    const g = {};
    for (const it of items) {
      const key = it.assigned_user_id || "unassigned";
      if (!g[key]) g[key] = { name: it.assigned_user_name || "Unassigned", items: [] };
      g[key].items.push(it);
    }
    return g;
  }, [items]);

  const action = async (path, sid) => {
    try { await api.post(`/admin/schedules/${sid}/${path}`); setRefreshKey((k) => k + 1); } catch {}
  };

  return (
    <AppShell>
      <div className="mb-6 cq-in">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Admin Console</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">Schedule</h1>
        <p className="text-slate-500 mt-1">Who's working on what, and when.</p>
      </div>

      {/* Today's Work */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mb-6">
        <TodaySection title="In Progress" icon={Play} tint="bg-blue-500" items={today.in_progress} action={action} testid="today-in-progress" onOpen={(id) => nav(`/admin/clients/${id}`)} />
        <TodaySection title="Next" icon={CalendarClock} tint="bg-amber-500" items={today.next} action={action} testid="today-next" onOpen={(id) => nav(`/admin/clients/${id}`)} />
        <TodaySection title="Later Today" icon={Clock} tint="bg-slate-500" items={today.later} action={action} testid="today-later" onOpen={(id) => nav(`/admin/clients/${id}`)} />
        <TodaySection title="Completed" icon={CheckCircle2} tint="bg-emerald-500" items={today.completed} action={action} testid="today-completed" onOpen={(id) => nav(`/admin/clients/${id}`)} done />
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-2xl border-slate-100 mb-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
            {[
              { v: "today", label: "Today" },
              { v: "tomorrow", label: "Tomorrow" },
              { v: "week", label: "This Week" },
            ].map((b) => (
              <button
                key={b.v}
                onClick={() => setRange(b.v)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${range === b.v ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
                data-testid={`range-${b.v}`}
              >{b.label}</button>
            ))}
          </div>
          <div className="sm:ml-auto min-w-[220px]">
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger data-testid="assignee-filter"><SelectValue placeholder="All team members" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All team members</SelectItem>
                {team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Calendar / list grouped by assignee */}
      <div className="space-y-4">
        {Object.entries(byAssignee).length === 0 && (
          <Card className="p-6 text-center text-slate-500" data-testid="schedule-empty-range">No scheduled work in this range.</Card>
        )}
        {Object.entries(byAssignee).map(([uid, group]) => (
          <Card key={uid} className="p-5 rounded-2xl border-slate-100" data-testid={`assignee-group-${uid}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><User size={16} /></div>
              <h3 className="font-display font-bold text-lg">{group.name}</h3>
              <span className="text-xs text-slate-500">· {group.items.length} scheduled</span>
            </div>
            <div className="space-y-2">
              {group.items.map((s) => (
                <div key={s.id} className="rounded-xl border border-slate-100 p-3 hover:bg-slate-50 flex items-start justify-between gap-2 flex-wrap" data-testid={`cal-item-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${STATUS_COLORS[s.status]} border-0 uppercase text-[10px] tracking-widest`}>{STATUS_LABEL[s.status] || s.status}</Badge>
                      <span className="text-sm font-semibold text-slate-900">{s.scheduled_start ? fmt(s.scheduled_start) : `${s.proposed_options?.length || 0} option(s) pending`}</span>
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">
                      <button className="font-semibold text-sky-700 hover:underline" onClick={() => nav(`/admin/clients/${s.client_id}`)} data-testid={`cal-open-client-${s.id}`}>{s.client_name || "Client"}</button>
                      {" · "}<span className="text-slate-600">{s.operation_type}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {s.status === "confirmed" && (
                      <Button size="sm" onClick={() => action("start", s.id)} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid={`cal-start-${s.id}`}><Play size={12} className="mr-1" /> Start</Button>
                    )}
                    {(s.status === "in_progress" || s.status === "confirmed") && (
                      <Button size="sm" onClick={() => action("complete", s.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`cal-complete-${s.id}`}><CheckCircle2 size={12} className="mr-1" /> Complete</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => nav(`/admin/clients/${s.client_id}`)} data-testid={`cal-open-${s.id}`}><ExternalLink size={12} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </AppShell>
  );
}

function TodaySection({ title, icon: Icon, tint, items, action, testid, onOpen, done }) {
  return (
    <Card className="p-4 rounded-2xl border-slate-100" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-xl ${tint} text-white flex items-center justify-center`}><Icon size={16} /></div>
        <h3 className="font-display font-bold text-lg">{title}</h3>
        <span className="ml-auto text-xs text-slate-500">{items?.length || 0}</span>
      </div>
      {(!items || items.length === 0) && <p className="text-xs text-slate-400">Nothing here.</p>}
      <div className="space-y-2">
        {(items || []).slice(0, 6).map((s) => (
          <div key={s.id} className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid={`${testid}-item-${s.id}`}>
            <div className="text-sm font-semibold text-slate-900 truncate">{s.client_name || "Client"}</div>
            <div className="text-xs text-slate-500 truncate">{s.operation_type}</div>
            <div className="text-xs text-slate-500">{done ? "Finished " + fmtTime(s.actual_end_at) : fmtTime(s.scheduled_start)}</div>
            <div className="text-xs text-slate-400">{s.assigned_user_name || "—"}</div>
            <div className="flex gap-1 mt-2">
              {s.status === "confirmed" && (
                <Button size="sm" className="h-7 px-2 text-xs bg-blue-500 hover:bg-blue-600 text-white" onClick={() => action("start", s.id)} data-testid={`${testid}-start-${s.id}`}>
                  <Play size={10} className="mr-1" /> Start
                </Button>
              )}
              {(s.status === "in_progress" || s.status === "confirmed") && (
                <Button size="sm" className="h-7 px-2 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => action("complete", s.id)} data-testid={`${testid}-complete-${s.id}`}>
                  <CheckCircle2 size={10} className="mr-1" /> Done
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onOpen(s.client_id)} data-testid={`${testid}-open-${s.id}`}>
                Open
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
